import {
  Component, EventEmitter, OnDestroy, OnInit, Output,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { LoadingSpinnerService } from 'src/app/services/loading-spinner.service';
import { SsoEndpoints, SsoEndpointsService } from 'src/app/services/sso-endpoints.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-log-in',
  templateUrl: './log-in.component.html',
  styleUrls: [ '../../sign-in.component.scss', './log-in.component.scss' ],
})
export class LogInComponent implements OnInit, OnDestroy {
  @Output() toggleForgotPasswordEvent = new EventEmitter<
    | 'forgotPasswordMode'
    | 'confirmationModeIsOn'
    | 'firstTimeMode'
    | 'basicSignInMode'
  >();
  @Output() authStatusEvent = new EventEmitter<{
    res: any;
    rememberMe: boolean;
    email: string;
    password: string;
  }>();
  rememberMeChecked = false;
  error$: Observable<any>;
  authSubscription: any;
  windowIsLarge = window.innerWidth > 450;
  rememberedEmail = '';
  entra_form_email = '';

  ssoEndpointOptions: SsoEndpoints[] = [];

  private errorSubscription: Subscription;
  private ssoSubscription: Subscription;

  constructor(
    private authService: AuthService,
    private loadingSpinnerService: LoadingSpinnerService,
    private ssoService: SsoEndpointsService,
  ) { }

  // TODO: make header nav links work, and update ui in general
  // TODO: make the forgot password link work
  // TODO: implement first time sign in flow
  // TODO: refine token handeling for timeout and refresh of user and permissions

  ngOnInit(): void {
    this.ssoService.removeSsoRequestingUser();
    this.errorSubscription = this.authService.error$.subscribe((error: any) => {
      this.error$ = error;
    });

    this.ssoSubscription = this.ssoService.ssoEndpointsData$.subscribe((ssoEndpoints: SsoEndpoints[]) => {
      if (ssoEndpoints) {
        this.ssoEndpointOptions = ssoEndpoints;
      }
    });
    this.ssoService.getSsoEndpoints();

    const loginName = localStorage.getItem('username') || '';

    if (loginName) {
      this.rememberedEmail = loginName;
      this.rememberMeChecked = true;
    }

    // defines google account, google login button, and places button on page
    // TODO: change this google button to use a Kalpa google cloud id instead of josh's. we perhaps also want to hide this as a secret

    google.accounts.id.initialize({
      client_id: environment.googleClientId as string,
      callback: this.handleGoogleLogin.bind(this),
      auto_select: false,
      cancel_on_tap_outside: true,
    });

    google.accounts.id.renderButton(document.getElementById('google_btn') as HTMLElement,
      {
        theme: 'outline',
        size: 'large',
        width: this.getGoogleButtonWidth(),
        shape: 'square',
        type: 'standard',
        logo_alignment: 'center',
      });
  }

  getGoogleButtonWidth() {
    const maxSize = 400;
    const forms = document.querySelector('.forms') as HTMLElement;
    const computedStyle = window.getComputedStyle(forms);
    const smallWidth =
      forms.offsetWidth -
      parseInt(computedStyle.paddingLeft) -
      parseInt(computedStyle.paddingRight);

    return (this.windowIsLarge ? maxSize : smallWidth).toString();
  }

  // this is our sign-in handler in the case of email signin.
  submitEmailLogin = async ({ form }: NgForm) => {
    // eject if somehow the form was submitted but is not valid
    if (!form.valid) {
      return;
    }

    this.loadingSpinnerService.setIsLoading(true);

    // consolidate the login data and send it to the auth provider to login
    const loginData = {
      email: form.value.email,
      password: form.value.password,
      rememberMe: form.value.rememberMe,
    };

    let res = await this.authService.handleEmailLogin(loginData,
      this.rememberMeChecked);

    if (res instanceof Error) {
      res = {
        challengeName: res.name,
        original: { ...res },
      };
    }

    if (res) {
      this.authStatusEvent.emit({
        res,
        rememberMe: this.rememberMeChecked,
        email: loginData.email,
        password: loginData.password,
      });
    }

    // form.reset();
  };

  public handleGoogleLogin = (response: any) => {
    try {
      this.loadingSpinnerService.setIsLoading(true);
      this.authService.handleGoogleLogin(response, this.rememberMeChecked);
    } catch (err) {
      this.loadingSpinnerService.setIsLoading(false);
      throw err;
    }
  };

  handleClaimAccount = (email: string, confirmationCode: string) => {
    this.authService.handleClaimAccount(email, confirmationCode);
  };

  toggleForgotPassword = (mode:
      | 'forgotPasswordMode'
      | 'confirmationModeIsOn'
      | 'firstTimeMode'
      | 'basicSignInMode') => {
    this.toggleForgotPasswordEvent.emit(mode);
  };

  submitForgotPasswordRequest = ({ form }: NgForm) => {
    console.log(form);
  };

  handleEntraLogin = (entra_email: string) => {
    try {
      this.ssoService.setSsoRequestingUser(entra_email);

      const urlParts = this.ssoService.setSsoRequestingUser(entra_email);

      const scope = environment.sso?.scope;
      const redirectOrigin = environment.sso?.redirectUri;
      const clientId = environment.sso?.clientId;
      const baseUrl = environment.sso?.cognitoDomain;

      const baseEndpoint = `${baseUrl}/authorize`;

      const params = new URLSearchParams({
        idp_identifier: urlParts.idpProvider,
        redirect_uri: redirectOrigin,
        response_type: 'code',
        client_id: clientId,
        scope: scope,
      });

      const url = `${baseEndpoint}?${params.toString()}`;

      window.location.assign(url);
    } catch (err) {
      this.loadingSpinnerService.setIsLoading(false);
      throw err;
    }
  };

  ngOnDestroy(): void {
    this?.errorSubscription?.unsubscribe();
  }
}
